<template>
  <!-- Primary Navigation -->
  <b-navbar type="light"
    variant="light"
    toggleable="sm"
    sticky
    data-name="forge-navigation">
    <b-navbar-brand href="/">
      <el-tooltip :content="`${apiName} ${appVersion} - ${gitVersion}`"
        :open-delay="500">
        <img src="@/assets/forge-logo.png"
          alt="Forge"
          width="25"
          class="pb-1">
      </el-tooltip>
    </b-navbar-brand>

    <b-navbar-toggle target="nav-collapse" />

    <b-collapse id="nav-collapse"
      is-nav>
      <!-- Menus -->
      <b-navbar-nav align="end">
        <template v-for="navItem in navigationList">
          <!-- No children -->
          <b-nav-item v-if="!navItem.children"
            :key="navItem.title"
            v-mp-access:[userGroups]="navItem.permissions"
            class="mr-4"
            :to="navItem.href">
            {{ navItem.title }}
          </b-nav-item>

          <!-- Children -->

          <b-nav-item-dropdown v-else
            :key="`child-${navItem.title}`"
            v-mp-access:[userGroups]="navItem.permissions"
            class="mr-4"
            right>
            <template #button-content>
              {{ navItem.title }}
            </template>
            <template v-for="(child, idx) in navItem.children">
              <b-dropdown-divider v-if="child.divider"
                :key="`divider-${idx}`" />
              <b-dropdown-item v-else
                :key="child.title"
                v-mp-access:[userGroups]="child.permissions"
                :to="child.href">
                {{ child.title }}
              </b-dropdown-item>
            </template>
          </b-nav-item-dropdown>
        </template>
      </b-navbar-nav>

      <!-- Search etc -->
      <b-navbar-nav class="ml-auto">
        <form id="searchForm"
          class="p-0 mr-2"
          inline
          @submit="onSearch">
          <div class="input-group">
            <label for="searchInput"
              class="sr-only">Search for anything</label>
            <input id="searchInput"
              v-model="searchText"
              accesskey="h"
              class="form-control rounded-left"
              type="search"
              placeholder="Search"
              aria-label="Search for anything"
              name="searchInput">
            <div class="input-group-append">
              <button type="submit"
                class="btn btn-dark"
                aria-label="Search"
                @click="onSearch">
                <font-awesome-icon icon="search" />
              </button>
            </div>
          </div>
        </form>

        <b-nav-item class="theme-toggle mr-2">
          <button class="btn btn-link theme-toggle-btn"
            :title="isDarkTheme ? 'Switch to light theme' : 'Switch to dark theme'"
            @click="toggleTheme">
            <font-awesome-icon :icon="isDarkTheme ? 'sun' : 'moon'" />
          </button>
        </b-nav-item>

        <b-nav-item-dropdown id="logoutBtn"
          variant="link"
          toggle-class="text-decoration-none"
          no-caret
          right>
          <template #button-content>
            <img ref="user-dropdown"
              :src="userProfilePic"
              :alt="myAccount.display_name"
              class="userPic rounded-circle">
          </template>
          <b-dropdown-item>
            <a v-b-toggle.myProfile
              href="/user/me"
              class="dropdown-item"
              replace
              @click.prevent>My Profile</a>
          </b-dropdown-item>
          <b-dropdown-item>
            <a href="/auth/authenticate"
              class="dropdown-item"
              @click.prevent="logout">Logout</a>
          </b-dropdown-item>
        </b-nav-item-dropdown>
      </b-navbar-nav>
    </b-collapse>
  </b-navbar>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { logout } from '@/API'

export default {
  name: 'AppNavigation',

  data() {
    return {
      searchText: '',
      navigationList: [
        { title: 'Dashboard', href: '/', permissions: [] },
        {
          title: 'Ecosystem',
          href: '/ecosystem/locations',
          permissions: [],
        },
        {
          title: 'Network',
          href: '/',
          permissions: ['noc', 'networks', 'finance', 'forge.patching', 'admin'],
          children: [
            { title: 'BGP Connection', href: '/resources/bgp-connection', permissions: ['noc', 'networks', 'finance', 'admin'] },
            { title: 'Devices', href: '/resources/device', permissions: ['noc', 'networks', 'finance', 'admin'] },
            { title: 'IP Transit Networks', href: '/resources/ip-transit-network', permissions: ['noc', 'networks', 'finance', 'admin'] },
            { title: 'IP Transit Blackholes', href: '/resources/ip-transit-blackhole', permissions: ['noc', 'networks', 'finance', 'admin'] },
            { title: 'Locations', href: '/resources/location', permissions: ['noc', 'networks', 'finance', 'admin'] },
            { title: 'Metros', href: '/resources/location-metro', permissions: ['noc', 'networks', 'finance', 'admin'] },
            { title: 'Organisations', href: '/resources/organisation', permissions: ['noc', 'networks', 'finance', 'admin'] },
            { title: 'Patch Panels', href: '/resources/patch-panel', permissions: ['noc', 'networks', 'finance', 'admin'] },
            { title: 'Patching', href: '/patching', permissions: ['forge.patching'] },
            { title: 'SMTP Exceptions', href: '/resources/ip-transit-smtp-exception', permissions: ['noc', 'networks', 'finance', 'admin'] },
            { title: 'Virtual Machine Images', href: '/resources/virtual-machine-image', permissions: ['noc', 'networks', 'finance', 'admin'] },
          ],
        },
        {
          title: 'Inventory',
          href: '/',
          permissions: ['noc', 'networks', 'finance', 'admin'],
          children: [
            { title: 'Colocation', href: '/resources/colocation' },
            { title: 'Cross Connects', href: '/resources/crossconnect' },
            { title: 'Circuits', href: '/resources/circuit' },
            { title: 'Supplier Rate Card', href: '/resources/supplier-rate-card' },
            { divider: true },
            { title: 'Asset Charges', href: '/inventory/asset-charges' },
          ],
        },
        {
          title: 'Administration',
          href: '/',
          permissions: ['admin'],
          children: [{ title: 'Users', href: '/users', permissions: ['admin'] }],
        },
      ],
      appVersion: import.meta.env.VITE_APP_VERSION,
      gitVersion: import.meta.env.VITE_GIT_VERSION,
    }
  },

  computed: {
    ...mapState('user', ['myAccount']),
    ...mapGetters('user', ['userGroups']),
    ...mapGetters('theme', ['isDarkTheme']),
    userProfilePic() {
      return this.myAccount.photo
    },
    apiName() {
      return window.envTitle
    },
  },

  methods: {
    ...mapActions('theme', ['setTheme']),
    toggleTheme() {
      const newTheme = this.isDarkTheme ? 'light' : 'dark'
      this.setTheme(newTheme)
    },
    async logout() {
      try {
        await logout()
        window.location = `${window.location.origin}/auth/authenticate`
      } catch (error) {
        this.$bvToast.toast(`[${error.status} ${error.statusText}] There was an issue during logout `, {
          title: 'logout',
          autoHideDelay: 5000,
          variant: 'danger',
          solid: true,
          noCloseButton: false,
          toaster: 'b-toaster-top-right',
        })
      }
    },
    onSearch(event) {
      event.preventDefault()
      this.$router.push({ name: 'search', params: { query: this.searchText } }).catch(() => {})
    },
  },
}
</script>

<style lang="scss" scoped>
.navbar {
  height: 60px;
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.2);
  background-color: var(--nav-bg);
  color: var(--nav-text);

  .navbar-brand {
    align-self: center;
  }

  input {
    pointer-events: all;
  }

  form {
    margin-top: 0.4rem;
  }

  .navbar-collapse.show {
    margin: 15px -15px;
    background-color: var(--nav-bg);
    padding: 1rem;
  }
}

.userPic {
  width: 30px;
  height: 30px;

  &:hover {
    cursor: pointer;
  }
}

.theme-toggle-btn {
  color: var(--nav-text);
  padding: 0.25rem 0.5rem;
  font-size: 1.4rem;

  &:hover {
    color: var(--link-color);
  }

  &:focus {
    box-shadow: none;
  }
}
</style>
